import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import { Button, Input, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import Swal from "sweetalert2";
import Header from "../../layout/Header/Header";
import { AuthContext } from "../../contexts/AuthContext";
import { posGet, posPost, posPatch, avisaError, mensajeError, numero, hoy, sumaDias, fechaHora, llave, variante } from "./posApi";
import { useTienda, TiendaSelector } from "./TiendaSelector";
import VariantePicker from "./VariantePicker";
import SinPermiso from "./SinPermiso";
import { ticketTraspaso } from "./posTickets";

const COLOR_ESTATUS = { "En tránsito": "warning", Recibido: "success", "Recibido con diferencias": "danger", Cancelado: "secondary" };
const COLOR_PEDIDO = { Pendiente: "warning", Enviada: "success", Rechazada: "danger", Cancelada: "secondary" };
const folioDe = (t, s) => `${t && t.clave ? `${t.clave}-` : ""}${s.folio}`;

function PosTraspasos() {
  const { user } = useContext(AuthContext);
  const puedeEnviar = !!(user && user.posTraspasos);
  const puedeRecibir = !!(user && user.posRecibir);
  const ctx = useTienda(false);
  const { tienda, nombreTienda } = ctx;
  const [tabElegida, setTab] = useState(null);
  // Pestaña inicial cuando ya se conocen los permisos
  const tab = tabElegida || (puedeEnviar ? "enviar" : "recibir");

  // Enviar
  const [destinos, setDestinos] = useState([]);
  const [destino, setDestino] = useState("");
  const [codigo, setCodigo] = useState("");
  const [renglones, setRenglones] = useState([]); // {k, articulos, colores, tallas, color, talla, codigo, nombre, cantidad, existencia}
  const [picker, setPicker] = useState(null);
  const [observaciones, setObservaciones] = useState("");
  const [enviando, setEnviando] = useState(false);
  const inputCodigo = useRef(null);

  // Recibir e historial
  const [pendientes, setPendientes] = useState([]);
  const [recibiendo, setRecibiendo] = useState(null); // traspaso + cantidades
  const [historial, setHistorial] = useState([]);
  const [inicio, setInicio] = useState(sumaDias(hoy(), -30));
  const [fin, setFin] = useState(hoy());
  const [tipo, setTipo] = useState("todos");
  const [codigoRec, setCodigoRec] = useState("");
  // Pedidos: los que le hacen a la tienda y los que ella hizo
  const [pedidosA, setPedidosA] = useState([]);
  const [pedidosHechos, setPedidosHechos] = useState([]);
  const [solicitud, setSolicitud] = useState(null); // pedido que se está enviando
  const inputRec = useRef(null);

  useEffect(() => { posGet("/tiendas", { destinos: "Si" }).then(setDestinos).catch(() => setDestinos([])); }, []);

  const cargar = useCallback(() => {
    if (!tienda) return;
    if (puedeRecibir) posGet("/traspasos", { area: tienda, tipo: "recibidos", estatus: "En tránsito" }).then(setPendientes).catch(() => setPendientes([]));
    posGet("/traspasos", { area: tienda, tipo, inicio, fin }).then(setHistorial).catch(avisaError);
    if (puedeEnviar) posGet("/solicitudes", { area: tienda, tipo: "recibidas", estatus: "Pendiente" }).then(setPedidosA).catch(() => setPedidosA([]));
    posGet("/solicitudes", { area: tienda, tipo: "hechas", inicio: sumaDias(hoy(), -30), fin: hoy() }).then(setPedidosHechos).catch(() => setPedidosHechos([]));
  }, [tienda, tipo, inicio, fin, puedeRecibir, puedeEnviar]);
  useEffect(() => { cargar(); }, [cargar]);

  // ---------- Enviar ----------
  const agregaVar = (a, v, cantidad = 1) => {
    const k = llave(a._id, v.colores, v.tallas);
    const ex = ((a.variantes || []).find((x) => llave(a._id, x.colores, x.tallas) === k) || {}).existencia || 0;
    setRenglones((prev) => {
      const ya = prev.find((r) => r.k === k);
      const n = (ya ? ya.cantidad : 0) + cantidad;
      if (n > ex) {
        Swal.fire({ icon: "warning", title: "Sin existencia", text: `${a.codigo} ${variante(v)}: hay ${ex}`, timer: 1800, showConfirmButton: false });
        return prev;
      }
      if (ya) return prev.map((r) => (r.k === k ? { ...r, cantidad: n } : r));
      return [...prev, { k, articulos: a._id, colores: v.colores, tallas: v.tallas, color: v.color, talla: v.talla, codigo: a.codigo, nombre: a.nombre, cantidad, existencia: ex }];
    });
  };

  const escanear = async (e) => {
    e.preventDefault();
    const q = codigo.trim();
    if (!q || !tienda) return;
    try {
      const r = await posGet("/articulos", { area: tienda, q });
      setCodigo("");
      if (!r.length) return Swal.fire({ icon: "info", title: "No encontrado", timer: 1500, showConfirmButton: false });
      const a = r[0];
      if (a.seleccion) agregaVar(a, a.seleccion); // etiqueta de una talla y color
      else if (a.conVariantes) setPicker(a);
      else agregaVar(a, a.variantes[0] || { colores: null, tallas: null });
    } catch (er) { avisaError(er); }
    setTimeout(() => inputCodigo.current && inputCodigo.current.focus(), 50);
  };

  const piezas = renglones.reduce((t, r) => t + (Number(r.cantidad) || 0), 0);
  const enviar = async () => {
    if (!destino) return Swal.fire({ icon: "warning", title: "Elige a qué tienda va" });
    const nombreDestino = (destinos.find((d) => d._id === destino) || {}).name;
    const ok = await Swal.fire({ title: "¿Enviar traspaso?", html: `<b>${numero(piezas)}</b> piezas de <b>${nombreTienda}</b> a <b>${nombreDestino}</b>.<br/>Salen de tu inventario ahora y entran al destino cuando lo reciban.`, icon: "question", showCancelButton: true, confirmButtonText: "Sí, enviar", cancelButtonText: "Revisar" });
    if (!ok.value) return;
    setEnviando(true);
    try {
      const t = await posPost("/traspasos", {
        area: tienda, destino, observaciones, solicitud: solicitud ? solicitud._id : undefined,
        renglones: renglones.map((r) => ({ articulos: r.articulos, colores: r.colores, tallas: r.tallas, cantidad: Number(r.cantidad) })),
      });
      ticketTraspaso(t);
      Swal.fire({ icon: "success", title: `Traspaso ${t.folio} enviado`, text: "Se imprimió la hoja para quien lleva la mercancía.", timer: 2200, showConfirmButton: false });
      setRenglones([]); setObservaciones(""); setDestino(""); setSolicitud(null);
      cargar();
    } catch (er) { Swal.fire({ icon: "error", title: "No se envió", text: mensajeError(er) }); } finally { setEnviando(false); }
  };

  // ---------- Pedidos ----------
  const prepararEnvio = async (p) => {
    if (renglones.length && !(await Swal.fire({ icon: "warning", title: "Ya tienes prendas en el envío", text: "Se van a reemplazar por las del pedido.", showCancelButton: true, confirmButtonText: "Reemplazar", cancelButtonText: "No" })).value) return;
    try {
      const det = await posGet(`/solicitudes/${p._id}`, { area: tienda });
      const sinExistencia = det.renglones.filter((r) => r.existencia <= 0);
      const cortos = det.renglones.filter((r) => r.existencia > 0 && r.existencia < r.cantidad);
      setRenglones(det.renglones.filter((r) => r.existencia > 0).map((r) => ({
        k: llave(r.articulos, r.colores, r.tallas), articulos: r.articulos, colores: r.colores, tallas: r.tallas, color: r.color, talla: r.talla,
        codigo: r.codigo, nombre: r.nombre, cantidad: Math.min(r.cantidad, r.existencia), existencia: r.existencia,
      })));
      setDestino(det.destino._id);
      setObservaciones(`Pedido ${folioDe(det.destino, det)} de ${det.destino.name}`);
      setSolicitud({ _id: det._id, folio: folioDe(det.destino, det), tienda: det.destino.name });
      setTab("enviar");
      if (sinExistencia.length || cortos.length) {
        Swal.fire({ icon: "info", title: "No alcanza todo",
          html: [...sinExistencia.map((r) => `${r.codigo} ${variante(r)}: no hay`), ...cortos.map((r) => `${r.codigo} ${variante(r)}: pidieron ${r.cantidad}, hay ${r.existencia}`)].join("<br/>") + "<br/><br/>Se envía lo que hay." });
      }
    } catch (er) { avisaError(er); }
  };
  const rechazarPedido = async (p) => {
    const r = await Swal.fire({ title: `¿Rechazar el pedido de ${p.destino.name}?`, input: "text", inputPlaceholder: "Motivo (ej. ya no hay, se apartó)", showCancelButton: true, confirmButtonText: "Rechazar", cancelButtonText: "No",
      inputValidator: (v) => (!v || !v.trim() ? "Escribe el motivo" : undefined) });
    if (!r.value) return;
    try { await posPatch(`/solicitudes/${p._id}/rechazar`, { area: tienda, motivo: r.value }); cargar(); } catch (er) { avisaError(er); }
  };
  const cancelarPedido = async (p) => {
    const r = await Swal.fire({ title: `¿Cancelar tu pedido a ${p.origen.name}?`, icon: "warning", showCancelButton: true, confirmButtonText: "Sí, cancelar", cancelButtonText: "No" });
    if (!r.value) return;
    try { await posPatch(`/solicitudes/${p._id}/cancelar`, { area: tienda }); cargar(); } catch (er) { avisaError(er); }
  };
  const resumenRenglones = (rs) => rs.map((r) => `${r.codigo}${variante(r) ? ` ${variante(r)}` : ""} ×${r.cantidad}`).join(", ");

  // ---------- Recibir ----------
  const abrirRecepcion = async (t) => {
    try {
      const det = await posGet(`/traspasos/${t._id}`, { area: tienda });
      // Arranca en ceros: se cuenta escaneando o capturando; "Llegó todo" llena lo enviado
      setRecibiendo({ ...det, cant: Object.fromEntries(det.renglones.map((r) => [llave(r.articulos, r.colores, r.tallas), "0"])), obs: "", ultimo: null });
      setCodigoRec("");
      setTimeout(() => inputRec.current && inputRec.current.focus(), 300);
    } catch (er) { avisaError(er); }
  };
  const llegoTodo = () => setRecibiendo({ ...recibiendo, cant: Object.fromEntries(recibiendo.renglones.map((r) => [llave(r.articulos, r.colores, r.tallas), String(r.cantidad)])) });
  const avisoRec = (title, text) => Swal.fire({ icon: "warning", title, text, timer: 2200, showConfirmButton: false });
  const escanearRec = async (e) => {
    e.preventDefault();
    const q = codigoRec.trim();
    setCodigoRec("");
    if (!q) return;
    const t = recibiendo;
    try {
      const r = await posGet("/articulos", { area: tienda, q });
      const a = r.find((x) => x.exacto) || null;
      const deModelo = a ? t.renglones.filter((x) => String(x.articulos) === String(a._id)) : [];
      // Etiqueta de talla y color, o código del modelo cuando el traspaso trae una sola variante de él
      const ren = !a ? null : a.seleccion
        ? deModelo.find((x) => llave(x.articulos, x.colores, x.tallas) === llave(a._id, a.seleccion.colores, a.seleccion.tallas))
        : deModelo.length === 1 ? deModelo[0] : null;
      if (!ren) {
        if (a && deModelo.length > 1 && !a.seleccion) avisoRec("Escanea la etiqueta de la prenda", `${a.codigo} viene en varias tallas o colores; el código del modelo no dice cuál es.`);
        else avisoRec("No viene en este traspaso", a ? `${a.codigo} ${a.seleccion ? variante(a.seleccion) : ""}` : q);
      } else {
        const k = llave(ren.articulos, ren.colores, ren.tallas);
        const n = parseInt(t.cant[k], 10) || 0;
        if (n >= ren.cantidad) avisoRec("Sobra una pieza", `De ${ren.codigo} ${variante(ren)} se enviaron ${ren.cantidad} y ya las contaste todas.`);
        else setRecibiendo({ ...t, cant: { ...t.cant, [k]: String(n + 1) }, ultimo: k });
      }
    } catch (er) { avisaError(er); }
    setTimeout(() => inputRec.current && inputRec.current.focus(), 50);
  };
  const recibir = async () => {
    const t = recibiendo;
    const total = Object.values(t.cant).reduce((s, n) => s + (parseInt(n, 10) || 0), 0);
    const faltan = t.piezas - total;
    if (!total) return Swal.fire({ icon: "warning", title: "No has contado nada", text: 'Escanea lo que llegó, captura las cantidades o usa "Llegó todo".' });
    const ok = await Swal.fire({
      title: faltan ? `¿Recibir con ${faltan} pieza(s) faltante(s)?` : "¿Todo llegó completo?",
      html: faltan ? "Lo que no llegó queda registrado como faltante del traspaso." : `Entran <b>${numero(total)}</b> piezas a tu inventario.`,
      icon: faltan ? "warning" : "question", showCancelButton: true, confirmButtonText: "Sí, recibir", cancelButtonText: "Revisar",
    });
    if (!ok.value) return;
    try {
      const r = await posPatch(`/traspasos/${t._id}/recibir`, {
        area: tienda, observaciones: t.obs,
        recibidos: t.renglones.map((x) => ({ articulos: x.articulos, colores: x.colores, tallas: x.tallas, cantidad: parseInt(t.cant[llave(x.articulos, x.colores, x.tallas)], 10) || 0 })),
      });
      Swal.fire({ icon: r.estatus === "Recibido" ? "success" : "warning", title: r.estatus, timer: 2000, showConfirmButton: false });
      setRecibiendo(null);
      cargar();
    } catch (er) { avisaError(er); }
  };

  // ---------- Historial ----------
  const imprimirHoja = async (t) => { try { ticketTraspaso(await posGet(`/traspasos/${t._id}`, { area: tienda })); } catch (er) { avisaError(er); } };
  const cancelar = async (t) => {
    const ok = await Swal.fire({ title: `¿Cancelar el traspaso ${t.folio}?`, text: "La mercancía regresa a tu inventario.", icon: "warning", showCancelButton: true, confirmButtonText: "Sí, cancelar", cancelButtonText: "No" });
    if (!ok.value) return;
    try { await posPatch(`/traspasos/${t._id}/cancelar`, { area: tienda }); cargar(); } catch (er) { avisaError(er); }
  };

  if (!(user && user.rolTiendas && (puedeEnviar || puedeRecibir))) return <SinPermiso que="hacer traspasos" />;

  const TABS = [
    puedeEnviar && ["enviar", "Enviar", "fas fa-truck"],
    puedeRecibir && ["recibir", `Por recibir${pendientes.length ? ` (${pendientes.length})` : ""}`, "fas fa-inbox"],
    ["pedidos", `Pedidos${pedidosA.length ? ` (${pedidosA.length})` : ""}`, "fas fa-paper-plane"],
    ["historial", "Historial", "fas fa-history"],
  ].filter(Boolean);

  return (
    <>
      <Header />
      <div className="card container col-12" style={{ padding: "18px 22px" }}>
        <div className="page-header">
          <div>
            <h3 className="page-title"><i className="fas fa-exchange-alt mr-2" style={{ color: "#8b5cf6" }} />Traspasos entre tiendas</h3>
            <p className="page-subtitle">Sale de una tienda al enviarse y entra a la otra cuando la recibe</p>
          </div>
          <div className="page-actions">
            <TiendaSelector ctx={ctx} />
            <Button color="danger" size="sm" outline href="/PosFaltantes"><i className="fas fa-search-minus mr-1" />Faltantes</Button>
            <Button color="secondary" size="sm" href="/PuntoVenta"><i className="fas fa-th-large mr-1" />Menú</Button>
          </div>
        </div>

        <div className="btn-group mb-3" role="group">
          {TABS.map(([k, l, ic]) => (
            <Button key={k} color={tab === k ? "primary" : "secondary"} outline={tab !== k} onClick={() => setTab(k)}><i className={`${ic} mr-1`} />{l}</Button>
          ))}
        </div>

        {tab === "enviar" && puedeEnviar && (
          <div className="form-section">
            <div className="form-section-body">
              {solicitud && (
                <div className="alert alert-info d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                  <i className="fas fa-paper-plane" />
                  <span>Estás enviando el pedido <strong>{solicitud.folio}</strong> de <strong>{solicitud.tienda}</strong>. Escanea o ajusta lo que mandas; al enviar, el pedido queda atendido.</span>
                  <Button size="sm" color="secondary" outline className="ml-auto" onClick={() => { setSolicitud(null); setRenglones([]); setDestino(""); setObservaciones(""); }}>Soltar pedido</Button>
                </div>
              )}
              <div className="filter-bar">
                <div style={{ minWidth: 240 }}><label>Va a la tienda</label>
                  <Input type="select" value={destino} disabled={!!solicitud} onChange={(e) => setDestino(e.target.value)}>
                    <option value="">Elige…</option>
                    {destinos.filter((d) => d._id !== tienda).map((d) => <option key={d._id} value={d._id}>{d.clave ? `${d.clave} · ` : ""}{d.name}</option>)}
                  </Input>
                </div>
                <form onSubmit={escanear} style={{ display: "flex", gap: 8, alignItems: "flex-end", flex: 1, minWidth: 260 }}>
                  <div style={{ flex: 1 }}><label>Escanea la etiqueta o el modelo</label><Input innerRef={inputCodigo} value={codigo} onChange={(e) => setCodigo(e.target.value)} placeholder="Código y Enter" /></div>
                  <Button color="primary" type="submit">Agregar</Button>
                </form>
              </div>
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead><tr><th>Modelo</th><th>Color · Talla</th><th className="text-right">Hay</th><th style={{ width: 110 }}>Envía</th><th /></tr></thead>
                  <tbody>
                    {!renglones.length && <tr><td colSpan={5} className="text-center text-muted py-3">Escanea las prendas que vas a enviar</td></tr>}
                    {renglones.map((r) => (
                      <tr key={r.k}>
                        <td><strong>{r.codigo}</strong> {r.nombre}</td>
                        <td>{variante(r) || "—"}</td>
                        <td className="text-right">{r.existencia}</td>
                        <td><Input bsSize="sm" type="number" min="1" max={r.existencia} value={r.cantidad} onChange={(e) => setRenglones(renglones.map((x) => (x.k === r.k ? { ...x, cantidad: Math.min(Math.max(parseInt(e.target.value, 10) || 1, 1), x.existencia) } : x)))} /></td>
                        <td><Button size="sm" color="danger" onClick={() => setRenglones(renglones.filter((x) => x.k !== r.k))}><i className="fas fa-times" /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Input className="mb-2" placeholder="Observaciones (ej. quién lo lleva)" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
              <div className="submit-bar">
                <span className="mr-auto text-muted">{numero(piezas)} pieza(s)</span>
                {renglones.length > 0 && <Button color="secondary" onClick={() => { setRenglones([]); setSolicitud(null); }}>Limpiar</Button>}
                <Button color="success" disabled={!renglones.length || enviando} onClick={enviar}><i className="fas fa-truck mr-1" />Enviar traspaso</Button>
              </div>
            </div>
          </div>
        )}

        {tab === "recibir" && puedeRecibir && (
          <div className="table-responsive">
            <table className="table table-sm table-dark-header">
              <thead><tr><th>Folio</th><th>Enviado</th><th>Viene de</th><th>Envió</th><th className="text-right">Piezas</th><th>Observaciones</th><th /></tr></thead>
              <tbody>
                {!pendientes.length && <tr><td colSpan={7} className="text-center text-muted py-4">No hay traspasos en camino a esta tienda</td></tr>}
                {pendientes.map((t) => (
                  <tr key={t._id}>
                    <td>{t.origen.clave ? `${t.origen.clave}-` : ""}{t.folio}</td><td>{fechaHora(t.fecha)}</td><td>{t.origen.name}</td>
                    <td>{t.user ? t.user.nombre : ""}</td><td className="text-right">{numero(t.piezas)}</td><td>{t.observaciones}</td>
                    <td><Button size="sm" color="success" onClick={() => abrirRecepcion(t)}><i className="fas fa-inbox mr-1" />Recibir</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "pedidos" && (
          <>
            {puedeEnviar && (
              <div className="form-section mb-3">
                <div className="form-section-header"><i className="fas fa-inbox" /><h4 className="form-section-title">Te piden</h4></div>
                <div className="form-section-body table-responsive">
                  <table className="table table-sm mb-0">
                    <thead><tr><th>Pedido</th><th>Fecha</th><th>Tienda que pide</th><th>Qué pide</th><th className="text-right">Piezas</th><th /></tr></thead>
                    <tbody>
                      {!pedidosA.length && <tr><td colSpan={6} className="text-center text-muted py-3">Ninguna tienda te ha pedido mercancía</td></tr>}
                      {pedidosA.map((p) => (
                        <tr key={p._id}>
                          <td>{folioDe(p.destino, p)}</td><td>{fechaHora(p.fecha)}</td><td>{p.destino.name}{p.user ? <div className="small text-muted">{p.user.nombre}</div> : null}</td>
                          <td style={{ fontSize: "0.82rem" }}>{resumenRenglones(p.renglones)}</td>
                          <td className="text-right">{numero(p.piezas)}</td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <Button size="sm" color="success" onClick={() => prepararEnvio(p)}><i className="fas fa-truck mr-1" />Preparar envío</Button>{" "}
                            <Button size="sm" color="danger" outline onClick={() => rechazarPedido(p)}>Rechazar</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <div className="form-section">
              <div className="form-section-header dark"><i className="fas fa-paper-plane" /><h4 className="form-section-title">Pediste (últimos 30 días)</h4></div>
              <div className="form-section-body table-responsive">
                <table className="table table-sm mb-0">
                  <thead><tr><th>Pedido</th><th>Fecha</th><th>Se le pidió a</th><th>Qué</th><th className="text-right">Piezas</th><th>Estatus</th><th /></tr></thead>
                  <tbody>
                    {!pedidosHechos.length && <tr><td colSpan={7} className="text-center text-muted py-3">Sin pedidos. Se piden desde Stock bajo.</td></tr>}
                    {pedidosHechos.map((p) => (
                      <tr key={p._id}>
                        <td>{folioDe(p.destino, p)}</td><td>{fechaHora(p.fecha)}</td><td>{p.origen.name}</td>
                        <td style={{ fontSize: "0.82rem" }}>{resumenRenglones(p.renglones)}</td>
                        <td className="text-right">{numero(p.piezas)}</td>
                        <td><span className={`badge badge-${COLOR_PEDIDO[p.estatus]}`}>{p.estatus === "Enviada" ? "Enviado" : p.estatus === "Rechazada" ? "Rechazado" : p.estatus === "Cancelada" ? "Cancelado" : "Pendiente"}</span>
                          {p.motivo ? <div className="small text-muted">{p.motivo}</div> : null}</td>
                        <td>{p.estatus === "Pendiente" && <Button size="sm" color="secondary" outline onClick={() => cancelarPedido(p)}>Cancelar</Button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {tab === "historial" && (
          <>
            <div className="filter-bar">
              <div><label>Ver</label>
                <Input type="select" value={tipo} onChange={(e) => setTipo(e.target.value)}>
                  <option value="todos">Enviados y recibidos</option><option value="enviados">Enviados</option><option value="recibidos">Recibidos</option>
                </Input>
              </div>
              <div><label>Desde</label><Input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} /></div>
              <div><label>Hasta</label><Input type="date" value={fin} onChange={(e) => setFin(e.target.value)} /></div>
            </div>
            <div className="table-responsive">
              <table className="table table-sm table-dark-header">
                <thead><tr><th>Folio</th><th>Fecha</th><th>De</th><th>A</th><th className="text-right">Enviadas</th><th className="text-right">Recibidas</th><th>Estatus</th><th /></tr></thead>
                <tbody>
                  {!historial.length && <tr><td colSpan={8} className="text-center text-muted py-4">Sin traspasos en el periodo</td></tr>}
                  {historial.map((t) => (
                    <tr key={t._id}>
                      <td>{t.origen.clave ? `${t.origen.clave}-` : ""}{t.folio}</td><td>{fechaHora(t.fecha)}</td>
                      <td>{t.origen.name}</td><td>{t.destino.name}</td>
                      <td className="text-right">{numero(t.piezas)}</td>
                      <td className="text-right">{t.estatus === "En tránsito" || t.estatus === "Cancelado" ? "—" : numero(t.piezasRecibidas)}</td>
                      <td><span className={`badge badge-${COLOR_ESTATUS[t.estatus]}`}>{t.estatus}</span>{t.observacionesRecepcion ? <div className="small text-muted">{t.observacionesRecepcion}</div> : null}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <Button size="sm" color="info" onClick={() => imprimirHoja(t)} title="Hoja de traspaso"><i className="fas fa-print" /></Button>{" "}
                        {puedeEnviar && t.estatus === "En tránsito" && t.origen._id === tienda && (
                          <Button size="sm" color="danger" onClick={() => cancelar(t)} title="Cancelar"><i className="fas fa-ban" /></Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <VariantePicker
        articulo={picker}
        modo="traspaso"
        onAgregar={(lista) => { lista.forEach((v) => agregaVar(picker, v, v.cantidad)); setPicker(null); }}
        onCerrar={() => setPicker(null)}
      />

      <Modal isOpen={!!recibiendo} toggle={() => setRecibiendo(null)} size="lg">
        <ModalHeader toggle={() => setRecibiendo(null)}>
          Recibir traspaso {recibiendo && recibiendo.folio} · de {recibiendo && recibiendo.origen.name}
        </ModalHeader>
        {recibiendo && (
          <ModalBody>
            <form onSubmit={escanearRec} className="d-flex align-items-end mb-2" style={{ gap: 8 }}>
              <div style={{ flex: 1 }}>
                <label className="small mb-1">Escanea cada prenda que llegó (o captura las cantidades)</label>
                <Input innerRef={inputRec} value={codigoRec} onChange={(e) => setCodigoRec(e.target.value)} placeholder="Etiqueta y Enter" />
              </div>
              <Button color="primary" type="submit">Contar</Button>
              <Button color="secondary" outline type="button" onClick={llegoTodo} title="Llena con lo enviado">Llegó todo</Button>
            </form>
            <div className="small mb-2">
              Contadas <strong>{numero(Object.values(recibiendo.cant).reduce((s, n) => s + (parseInt(n, 10) || 0), 0))}</strong> de <strong>{numero(recibiendo.piezas)}</strong>
            </div>
            <table className="table table-sm">
              <thead><tr><th>Modelo</th><th>Color · Talla</th><th className="text-right">Enviadas</th><th style={{ width: 110 }}>Llegaron</th></tr></thead>
              <tbody>
                {recibiendo.renglones.map((r) => {
                  const k = llave(r.articulos, r.colores, r.tallas);
                  const n = parseInt(recibiendo.cant[k], 10) || 0;
                  return (
                    <tr key={k} style={recibiendo.ultimo === k ? { background: "#dbeafe" } : n === r.cantidad ? { background: "#ecfdf5" } : {}}>
                      <td><strong>{r.codigo}</strong> {r.nombre}</td><td>{variante(r) || "—"}</td>
                      <td className="text-right">{r.cantidad}</td>
                      <td><Input bsSize="sm" type="number" min="0" max={r.cantidad} value={recibiendo.cant[k]}
                        onChange={(e) => setRecibiendo({ ...recibiendo, cant: { ...recibiendo.cant, [k]: String(Math.min(Math.max(parseInt(e.target.value, 10) || 0, 0), r.cantidad)) } })} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Input placeholder="Observaciones (ej. llegó una prenda dañada)" value={recibiendo.obs} onChange={(e) => setRecibiendo({ ...recibiendo, obs: e.target.value })} />
          </ModalBody>
        )}
        <ModalFooter>
          <Button color="secondary" onClick={() => setRecibiendo(null)}>Cancelar</Button>
          <Button color="success" onClick={recibir}><i className="fas fa-check mr-1" />Recibir</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default PosTraspasos;
